import { ClientConfig, SSOConfig, SSOCrypto } from './types';
import { LoggerNames, LogLevel } from './lib/constants';
import { TokenManager } from './token-manager/token-manager';
import { StateVerifier } from './oidc/state-verifier';
import { PkceManager } from './oidc/pkce-manager';
import { Logger } from './lib/logger';
import { OidcConfigManager } from './oidc/oidc-config-manager';
import { V2Router } from './router/v2.router';
import { AccessToken, ITokenManager, SSOTokens } from './token-manager/token-manager.types';
import { StorageManager } from './storage-manager/storage-manager';
import { AuthState, AuthStateManager, IAuthStateManager } from './auth-state-manager/auth-state-manager';
import { validateScope } from './lib/scope';
import { ServiceManager } from './services/service-manager';
import { SSO_TRANSACTION_KEYS } from './storage-manager/storage.const';
import { url } from './lib/url';
import { AuthSdkError } from './lib/auth-sdk-error';
import { exchangeCodeForTokens } from './oauth/oauth';
import { Authn } from './authn/authn';
import { TransactionManager } from './transaction/transaction-manager';

/**
 * The MapSSO class is the main entry point for the auth sdk.
 * It provides an interface for initializing authorization flows
 * and managing authorization state.
 *
 * The type of AuthState can be customized by passing an AuthState type to the `TAuthState` generic parameter. The custom AuthState must
 * extend the {@link AuthState} interface. You must also supply a {@link SSOConfig#transformAuthState} function that returns your
 * custom AuthState type. See {@link AuthStateManager} for more details.
 *
 * @property {AuthStateManager} authStateManager - The auth state manager instance. Use this to subscribe to auth state changes.
 * @property {TokenManager} tokenManager - The token manager instance. Use this to subscribe to token changes, or to retrieve tokens from storage.
 */
export class MapSSO<TAuthState extends AuthState = AuthState> {
  private readonly clientConfig: ClientConfig;
  private readonly crypto: SSOCrypto;
  private readonly oidcManager: OidcConfigManager;
  private readonly logger: Logger;
  private readonly pkceManager: PkceManager;
  private readonly stateVerifier: StateVerifier;
  private readonly v2Router: V2Router;
  private readonly storageManager: StorageManager;
  private readonly transactionManager: TransactionManager;
  private readonly serviceManager: ServiceManager;

  public readonly authStateManager: IAuthStateManager<TAuthState>;
  public readonly tokenManager: ITokenManager;

  /**
   * Creates a new MapSSO instance.
   * @param clientId -See {@link SSOConfig#clientId
   * @param crypto - See {@link SSOConfig#crypto}
   * @param redirectUri - See {@link SSOConfig#redirectUri}
   * @param postLogoutRedirectUri - See {@link SSOConfig#postLogoutRedirectUri}
   * @param storage - See {@link SSOConfig#storage}
   * @param logLevel - See {@link SSOConfig#logLevel} or {@link LogLevel}
   * @param issuer - See {@link SSOConfig#issuer}
   * @param services - See {@link SSOConfig#clientId}
   * @param transformAuthState - See {@link SSOConfig.transformAuthState}
   */
  constructor({
    cookies,
    clientId,
    crypto = window.crypto,
    issuer,
    logLevel = LogLevel.WARN,
    postLogoutRedirectUri = window.origin,
    redirectUri,
    services,
    storage,
    transformAuthState,
  }: SSOConfig<TAuthState>) {
    this.clientConfig = {
      issuer,
      postLogoutRedirectUri,
      clientId,
      redirectUri,
    };

    this.logger = Logger.initLogger(LoggerNames.MAP_SSO, logLevel);
    this.logger.debug('Initializing MapSSO');
    this.storageManager = new StorageManager(storage, cookies);
    this.crypto = crypto;
    this.oidcManager = new OidcConfigManager({ issuer, cacheStorage: this.storageManager.getCacheStorage() });
    this.stateVerifier = new StateVerifier({ crypto, transactionStorage: this.storageManager.getTransactionStorage() });
    this.pkceManager = new PkceManager({
      crypto: this.crypto,
      transactionStorage: this.storageManager.getTransactionStorage(),
    });
    this.v2Router = new V2Router({ issuer, oidcManager: this.oidcManager });
    this.tokenManager = new TokenManager({
      config: this.clientConfig,
      tokenStorage: this.storageManager.getTokenStorage(),
      router: this.v2Router,
    });
    this.authStateManager = new AuthStateManager<TAuthState>({ transformAuthState, sdk: this });
    this.serviceManager = new ServiceManager(this.tokenManager, this.v2Router, this.authStateManager, services);
    this.transactionManager = TransactionManager.getSingleton();
  }

  /**
   * Start the authorization service. This will initialize background services
   * to manage things like token events, auto-renew and session logout. See {@link ServiceManager} and {@link TokenManager}
   * for more details.
   *
   * Will trigger an auth state update.
   *
   * @returns {Promise<void>}
   */
  public async start(): Promise<void> {
    this.tokenManager.start();
    await this.serviceManager.start();
  }

  /**
   * Stop the authorization service.
   * This will stop all background services in the {@link ServiceManager} as well
   * as the {@link TokenManager} events.
   * Emits an auth state update.
   *
   * @returns {Promise<void>}
   */
  public async stop(): Promise<void> {
    await this.serviceManager.stop();
    this.tokenManager.stop();
  }

  /**
   * RP-Initiated Logout. This will redirect the client's browser to the authorization server where
   * the user will be logged out and the session destroyed.
   *
   * Marks all tokens in storage as pending removal to be cleaned up at next sdk initialization.
   *
   * @param postLogoutRedirectUri - The URI to redirect to after logout. Defaults to the current origin.
   * @param state - User defined state. Will be returned in the logout response.
   * @returns {Promise<void>} - Redirects the browser
   */
  public async signOut({
    postLogoutRedirectUri = window.origin,
    state = '',
  }: { postLogoutRedirectUri?: string; state?: string } = {}): Promise<void> {
    const idToken = this.tokenManager.idToken;
    if (!idToken?.token) {
      this.logger.warn('User is not logged in. Missing idToken');
      return;
    }
    return await this.transactionManager.create('logout', async () => {
      // keep the tokens in storage until the logout redirect is complete.
      // pending removal tokens will be cleaned up by the token manager when started.
      this.tokenManager.addPendingRemovalFlags();
      this.tokenManager.stop();
      const logoutUrl = await this.v2Router.logout({
        post_logout_redirect_uri: postLogoutRedirectUri,
        state: this.stateVerifier.generate(state),
        id_token_hint: idToken.token,
        client_id: this.clientConfig.clientId,
      });
      return window.location.assign(logoutUrl);
    });
  }

  /**
   * Uses the current auth state to determine if the user is authenticated.
   * If the `onExpiredToken` option is set to 'renew' or autoRenew is enabled, will attempt to renew the tokens if expired.
   * If the `onExpiredToken` option is set to 'remove', will clear the tokens from storage.
   * @param onExpiredToken
   * @returns {boolean}
   */
  public async isAuthenticated({ onExpiredToken }: { onExpiredToken?: 'renew' | 'remove' } = {}) {
    const shouldRenew = Boolean(onExpiredToken === 'renew' || this.serviceManager.options.autoRenew);
    const shouldRemove = onExpiredToken === 'remove';

    const authState = await this.authStateManager.updateAuthState();

    // try to renew the token
    if (!authState?.isAuthenticated) {
      if (shouldRemove) {
        this.tokenManager.clear();
        return false;
      } else if (authState && shouldRenew) {
        try {
          const renewedTokens = await this.tokenManager.renew();

          return Boolean(renewedTokens);
        } catch {
          // token manager will emit the error
          this.tokenManager.clear();
          return false;
        }
      }
    }

    // token is not expired
    return Boolean(authState?.isAuthenticated);
  }

  /**
   * Creates a new OIDC authorization request and redirects the user to the authorization server to authenticate.
   * The user will be returned to the `redirect_uri` specified in the client config.
   *
   * @param state - The state parameter to use for the authorization request. This is used to prevent CSRF attacks.
   *                The auth sdk will always generate a cryptographically safe random state parameter.
   *                Use this option if you need to pass additional custom state for your authentication flow.
   *                Any state you provide will be returned from the `handleRedirect` method, or provided as a query parameter to the `originalUri` if set.
   * @param scope - The scopes that are requested for this access token. By default, the auth sdk will include the `openid` scope.
   *                Please specify any additional scopes your application requires.
   *                See: https://datatracker.ietf.org/doc/html/rfc6749#section-3.3 for more information.
   *
   * @returns {Promise<void>} - Redirects the browser
   *
   * @throws {AuthSdkError} - if it fails to initialize the redirect flow.
   */
  public async signInWithRedirect({ state = '', scope = 'openid' }: { state?: string; scope?: string } = {}) {
    return await this.transactionManager.create('login', async () => {
      try {
        this.logger.debug('Initializing redirect flow');
        const { clientId, redirectUri } = this.clientConfig;
        const { codeChallenge } = await this.pkceManager.createCodePair();
        const url = await this.v2Router.login({
          prompt: 'display',
          client_id: clientId,
          redirect_uri: redirectUri,
          scope: validateScope(scope ?? 'openid'),
          state: this.stateVerifier.generate(state),
          code_challenge: codeChallenge,
          code_challenge_method: await this.oidcManager.codeChallengeMethod(this.pkceManager.CODE_METHOD),
          nonce: await this.stateVerifier.createNonce(),
          response_type: await this.oidcManager.responseType('code'),
          response_mode: await this.oidcManager.responseMode('query'),
        });
        return window.location.assign(url);
      } catch (e) {
        this.logger.error('Failed to initialize redirect flow', e);
        throw new AuthSdkError('Failed to initialize redirect flow', e);
      }
    });
  }

  /**
   * Returns true if the current route is a valid login redirect uri.
   * Checks the current location against the registered redirect uri and verifies there is an auth code parameter.
   *
   * @returns {boolean}
   */
  public isLoginRedirect() {
    const isRedirect = url.locationWithoutQueryString() === this.clientConfig.redirectUri && url.hasAuthCode();
    this.logger.debug(`Checking for redirect route: ${isRedirect}`);
    return isRedirect;
  }

  /**
   * Should be called by the redirect_uri page. Will handle extracting an authorization code from the url,
   * and exchanging it for an access token.
   *
   * Accepts an optional `redirectParams` object, which can be used to pass in the `code` and `state` parameters
   * manually. If no `redirectParams` are given, it will read them from the current url.
   *
   * This function is not idempotent. Auth codes can only be exchanged once. Be sure to only invoke this method
   * once per auth code, or subsequent calls will fail.
   *
   * If an `originalUri` had been set via {@link setOriginalUri}, the user will be redirected to that uri after a successful login.
   * If no `originalUri` was present, the function will return the tokens and state from the authorization response.
   *
   * @param redirectParams
   *
   * @returns {Promise<{ tokens: SSOTokens; state: string }>} - Returns the tokens and state from the authorization response.
   * @throws {AuthSdkError} - If multiple calls are made to this method before token exchange has been completed or token exchange fails.
   */
  public async handleRedirect(redirectParams?: {
    code: string;
    state: string | null;
  }): Promise<{ tokens: SSOTokens; state: string }> {
    return this.transactionManager.create('handle-redirect', async () => {
      try {
        redirectParams ||= this.v2Router.parseRedirectUrl();
        const stateStr = this.stateVerifier.verify(redirectParams.state);
        const tokens = await exchangeCodeForTokens({
          clientConfig: this.clientConfig,
          code: redirectParams.code,
          stateVerifier: this.stateVerifier,
          router: this.v2Router,
          pkce: this.pkceManager,
          tokenManager: this.tokenManager,
        });
        const state = this.stateVerifier.decode(stateStr);
        const originalUri = this.getOriginalUri();
        if (originalUri) {
          const url = new URL(originalUri);
          url.searchParams.append('state', state);
          this.removeOriginalUri();
          window.location.replace(originalUri);
        }
        return { tokens, state };
      } catch (e) {
        this.logger.error('Failed to exchange auth code for tokens', e);
        throw new AuthSdkError('Failed to exchange auth code for tokens', e);
      }
    });
  }

  /**
   * Used to set the original uri that the user was trying to access before being redirected to the authorization server.
   * If provided, the user will be redirected to this uri after a successful login and token exchange.
   *
   * Example Flow:
   * 1. User navigates to /protected-route
   * 2. sdk.setOriginalUri('/protected-route')
   * 3. sdk.signInWithRedirect({ state: 'user-provided-state' })
   * 4. User is redirected to the authorization server to authenticate
   * 6. User is redirected back to the registered redirect_uri
   * 7. sdk.handleRedirect()
   * 8. sdk exchanges auth code for tokens
   * 9. sdk redirects the user to the original uri: /protected-route?state=user-provided-state
   *
   * @param uri
   *
   * @returns {void}
   */
  setOriginalUri(uri: string | URL) {
    const storage = this.storageManager.getTransactionStorage();
    const originalUri = uri instanceof URL ? uri.toString() : uri;
    storage.setItem(SSO_TRANSACTION_KEYS.ORIGINAL_URI, originalUri);
  }

  /**
   * Returns the current `originalUri` if set.
   *
   * return {string | null}
   */
  getOriginalUri(): string | null {
    return this.storageManager.getTransactionStorage().getItem(SSO_TRANSACTION_KEYS.ORIGINAL_URI);
  }

  /**
   * Removes the `originalUri` from storage.
   *
   * @returns {void}
   */
  removeOriginalUri() {
    this.storageManager.getTransactionStorage().removeItem(SSO_TRANSACTION_KEYS.ORIGINAL_URI);
  }

  /**
   * Set custom headers to be sent with all requests to the authorization server.
   * Note that this cannot be used to overwrite headers set by the auth sdk.
   * Be aware that disallowed headers may be blocked by CORS, resulting in a failed request.
   * @param headers
   *
   * @returns {void}
   */
  setHeaders(headers: Record<string, string>) {
    this.v2Router.setHeaders(headers);
  }

  /**
   * Retrieve details about the current user from the authorization server.
   * Requires an access token to be present in storage.
   *
   * @returns {Promise<V2UserInfoResponse>}
   *
   * @throws {AuthSdkError} - if no tokens are present or the token has expired
   * @throws {NetworkError} - if the network request fails
   */
  getUserInfo(accessToken?: AccessToken) {
    const token = accessToken ?? this.tokenManager.accessToken;
    if (!token || this.tokenManager.hasExpired(token)) {
      throw new AuthSdkError('Invalid or expired access token');
    }
    return this.v2Router.userInfo({ accessToken: token.token });
  }

  /**
   * Parse oauth raw state param from the url. Can be used to extract the oauth state from urls after login/logout
   * redirect. If no state string is provided, it will attempt to parse the `state` query param from the current url.
   *
   * Note: This method does NOT verify the state string. The state is verified by the `handleRedirect` method.
   * @param state - The encoded state string to parse
   * @throws AuthSdkError - if no state string is provided and no state param is found in the current url
   */
  parseOAuthState(state?: string): string {
    const stateStr = state ?? new URLSearchParams(window.location.search).get('state');
    if (!stateStr) {
      throw new AuthSdkError('Missing state parameter');
    }
    return this.stateVerifier.decode(stateStr);
  }

  /**
   * Checks if the user's session is currently active. Returns a boolean.
   * Requires a valid access token. If no access token is provided or
   * present in token storage, it will return false.
   */
  async checkSession(accessToken?: string) {
    const token = accessToken ?? this.tokenManager.accessToken?.token;
    if (!token) return false;
    return this.v2Router.checkSession({ accessToken: token });
  }

  /**
   * Returns the raw access token string from storage
   * The decoded access token can be retrieved from the {@link TokenManager}
   */
  getAccessToken(): string | null {
    return this.tokenManager.accessToken?.token ?? null;
  }

  /**
   * Returns the raw id token string from storage
   * The decoded id token can be retrieved from the {@link TokenManager}
   */
  getIdToken(): string | null {
    return this.tokenManager.idToken?.token ?? null;
  }

  /**
   * Returns the raw refresh token string from storage
   */
  getRefreshToken(): string | null {
    return this.tokenManager.refreshToken?.token ?? null;
  }

  /**
   * @deprecated - Will not work without 3rd party cookies. Use PKCE Redirect Flow instead
   */
  get authn(): Authn {
    return new Authn({
      router: this.v2Router,
      pkceManager: this.pkceManager,
      stateVerifier: this.stateVerifier,
      clientConfig: this.clientConfig,
      tokenManager: this.tokenManager,
    });
  }
}
